// @ts-strict-ignore
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import type { HTMLProps } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@aviary/components/Tooltip";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { l } from "@aviary/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { DeprecatedQuantitySelector } from "./DeprecatedQuantitySelector";

import * as styles from "./QuantitySelector.styles";

interface Props extends HTMLProps<HTMLInputElement> {
  /**
   * Value for Quantity
   *
   * @required
   */
  value: number;
  /**
   * Callback on value change
   *
   * @required
   */
  handleSetValue: (quantity?: number) => void;
  /**
   * Disables the buttons/input
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Toggles a small version
   *
   * @default false
   */
  isSmall?: boolean;
  /**
   * Specify a minimum number enterable by the user
   */
  minValue?: number;
  /**
   * Specify a maximum number enterable by the user
   */
  maxValue?: number;
  /**
   * An object containing custom aria-labels
   */
  labels?: {
    main?: string;
    input?: string;
    buttonIncrement?: string;
    buttonDecrement?: string;
  };
  /**
   * Toggles if it has margin around it
   *
   * @default false
   */
  hasMargin?: boolean;
  /**
   *  Unique id for e2e testing purposes
   */
  incrementE2eData?: string;
  /**
   *  Optional icon to be rendered
   */
  icon?: IconDefinition;
  /**
   *  Optional tooltip to describe icon
   */
  iconTooltipContent?: string;
  /**
   *  Makes the component full width
   */
  isFullWidth?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/inputs/QuantitySelector
 */
const QuantitySelector = (props: Props) => {
  const {
    value,
    handleSetValue,
    isSmall = false,
    minValue = 1,
    maxValue = 999,
    disabled = false,
    labels,
    hasMargin,
    incrementE2eData,
    icon,
    iconTooltipContent,
    isFullWidth = false,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState<number>(value);
  const [isActive, setIsActive] = useState<boolean>(false);
  const isDS4Enabled = useIsDS4Enabled();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  if (!isDS4Enabled) {
    return (
      <DeprecatedQuantitySelector
        {...{ isSmall, minValue, maxValue, disabled, isFullWidth, ...props }}
      />
    );
  }

  const buildWrapper = [
    styles.wrapper.base,
    hasMargin && styles.wrapper.hasMargin,
    isActive && styles.wrapper.base,
    isSmall && styles.wrapper.isSmall,
    icon && styles.wrapper.hasIcon,
    isFullWidth && styles.wrapper.isFullWidth,
  ];

  const buttonStyles = (buttonStyle, iconButtonStyle?) => {
    return [buttonStyle, isActive && styles.button.active, icon && iconButtonStyle];
  };

  const inputStyles = [styles.input.content, isActive && styles.input.active];

  const handleIncrement = () => {
    if (!maxValue || value < maxValue || maxValue < minValue) {
      handleSetValue(value + 1);
    }
  };
  const handleDecrement = () => {
    if (value > minValue) {
      handleSetValue(value - 1);
    }
  };

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleUnfocus = () => {
    setIsActive(false);
  };

  const handleKeyPress = ({ key }) => {
    switch (key) {
      case "Enter":
        handleBlur();
        break;
      default:
        break;
    }
  };

  const handleChange = ({ target }) => {
    const inputValue = target.value;
    const parsedValue = inputValue <= maxValue ? parseInt(inputValue) : maxValue;
    if (value) {
      handleSetValue(parsedValue);
      setLocalValue(parsedValue);
    } else {
      handleSetValue(parsedValue);
      setLocalValue(inputValue);
    }
  };

  const resetLocalValue = (propValue: number, newValue: number) => {
    /* To force re-render when the prop.value is same as newValue */
    if (propValue === newValue) {
      setLocalValue(newValue);
    }
  };

  const handleBlur = () => {
    if (!localValue || localValue < minValue) {
      handleSetValue(minValue);
      resetLocalValue(value, minValue);
    } else if (maxValue && localValue > maxValue && maxValue > minValue) {
      handleSetValue(maxValue);
      resetLocalValue(value, maxValue);
    } else {
      handleSetValue(localValue);
    }
  };

  const getValue = () => {
    if (localValue) {
      return localValue;
    }

    return 0;
  };

  const isIncrementDisabled = () => {
    return (maxValue >= minValue && getValue() >= maxValue) || disabled;
  };

  const getIconSize = () => {
    if (isSmall) return "sm";

    return "lg";
  };

  const getIcon = () => {
    if (icon) {
      return (
        <div css={[styles.icon, isSmall && styles.iconSmall, isActive && styles.button.active]}>
          <Tooltip tooltipContent={iconTooltipContent} isTooltipHidden={!iconTooltipContent}>
            <FontAwesomeIcon size={getIconSize()} icon={icon} />
          </Tooltip>
        </div>
      );
    }
  };

  return (
    <div
      css={buildWrapper}
      role="spinbutton"
      aria-label={labels?.main ?? t(l.aviary.quantitySelector.itemQuantityWidget)}
      aria-valuenow={getValue()}
      aria-valuemin={minValue}
      aria-valuemax={maxValue}
      onFocus={handleFocus}
      onBlur={handleUnfocus}
    >
      <div
        css={[
          styles.button.wrapper,
          icon && styles.button.hasIcon,
          isFullWidth && styles.button.isFullWidth,
        ]}
      >
        {getIcon()}
        <button
          css={buttonStyles(styles.leftButton, styles.leftButtonWithIcon)}
          type="button"
          onClick={handleDecrement}
          disabled={getValue() === minValue || disabled}
          aria-label={labels?.buttonDecrement ?? t(l.aviary.quantitySelector.buttonDecrement)}
          onBlur={handleBlur}
          data-testid="value-decrement"
        >
          <FontAwesomeIcon icon={faMinus} />
        </button>
      </div>
      <div css={[styles.input.wrapper, isFullWidth && styles.input.hasMinWidth]}>
        <input
          css={inputStyles}
          inputMode="numeric"
          pattern="[0-9]*"
          type="text"
          value={getValue()}
          onChange={handleChange}
          disabled={disabled}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          aria-label={labels?.input ?? t(l.aviary.quantitySelector.specifyItemQuantity)}
          data-e2e={`${disabled ? "disabled-" : ""}product-quantity-input`}
          {...rest}
        />
      </div>
      <div css={[styles.button.wrapper, isFullWidth && styles.button.isFullWidth]}>
        <button
          css={buttonStyles(styles.rightButton)}
          type="button"
          onClick={handleIncrement}
          disabled={isIncrementDisabled()}
          aria-label={labels?.buttonIncrement ?? t(l.aviary.quantitySelector.buttonIncrement)}
          data-testid="value-increment"
          data-e2e={incrementE2eData || "value-increment"}
          onBlur={handleBlur}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
    </div>
  );
};

export { QuantitySelector };
